import type { ZodType } from "zod";
import {
  ApiRequestError,
  NETWORK_UNREACHABLE,
  networkUnreachableMessage,
} from "@/lib/api/api-error";
import { connectRefusalFromText } from "@/lib/api/connect-refusal";
import { apiFetch } from "@/lib/api-fetch";
import { preparingMessage } from "@/lib/operational-state";

// Typed JSON layer over apiFetch: builds the request, throws a labelled error
// on non-2xx, and (optionally) validates the body against a shared contract.
// Deliberately a SEPARATE module from api-fetch so tests that mock apiFetch
// intercept the cross-module import — an intra-module call wouldn't be mocked.

interface SendInit {
  /**
   * JSON-encoded into the body; sets `Content-Type: application/json`.
   *
   * Typed as an object, not `unknown`, so a caller that encodes it itself is a
   * type error here rather than a 400 from the server: this layer does the
   * encoding, and an already-encoded body arrives at the route as a JSON
   * string where its schema expects an object.
   */
  body?: object;
  /** Overrides apiFetch's session credentials; the connect pages send "omit". */
  credentials?: RequestCredentials;
  headers?: Record<string, string>;
  method?: "GET" | "POST" | "PATCH" | "PUT" | "DELETE";
  /** Appended as a query string; `undefined` / `null` entries are skipped. */
  query?: Record<string, string | number | boolean | undefined | null>;
  /** Cancels the request; a cancellation rejects with its own reason. */
  signal?: AbortSignal;
}

interface ApiRequestInit extends SendInit {
  /** Human label for the thrown error, e.g. "Create property". */
  label: string;
}

/**
 * {@link apiFetch} with the request built (query string, JSON body) and a
 * request that never reached the server thrown as an {@link ApiRequestError}
 * carrying the house connection sentence. Returns the response whatever its
 * status: for a caller that words the server's refusal itself.
 */
export async function sendApiRequest(
  path: string,
  { method = "GET", body, credentials, headers, query, signal }: SendInit = {}
): Promise<Response> {
  let url = path;
  if (query) {
    const search = new URLSearchParams();
    for (const [key, value] of Object.entries(query)) {
      if (value !== undefined && value !== null) {
        search.set(key, String(value));
      }
    }
    const queryString = search.toString();
    if (queryString) {
      url += `?${queryString}`;
    }
  }
  try {
    return await apiFetch(url, {
      body: body === undefined ? undefined : JSON.stringify(body),
      credentials,
      headers:
        body === undefined
          ? headers
          : { ...headers, "Content-Type": "application/json" },
      method,
      signal,
    });
  } catch (cause) {
    if (signal?.aborted) {
      throw cause;
    }
    // `fetch` rejects only when the request never completed — offline, DNS,
    // TLS, a dropped connection. Its message is a browser internal ("Failed to
    // fetch"), and callers surface `error.message` straight to the user, so
    // without this a host on a bad signal reads engine jargon. Status 0 marks
    // "never reached the server", distinct from any real HTTP code.
    // biome-ignore lint/style/useErrorCause: it IS preserved — ApiRequestError forwards `cause` to `super(message, { cause })`; the rule only recognises the option passed directly to `new Error`.
    throw new ApiRequestError({
      cause,
      detail: cause instanceof Error ? cause.message : String(cause),
      status: NETWORK_UNREACHABLE,
      userMessage: networkUnreachableMessage(),
    });
  }
}

/**
 * {@link sendApiRequest} plus the shared "throw on non-2xx" tail: a non-2xx
 * response throws an {@link ApiRequestError} whose `message` is a user-safe
 * string and whose raw body is kept on `detail` (never the user-facing
 * message). Returns the ok {@link Response}; use {@link apiJson} for the
 * parsed (and optionally validated) body.
 */
export async function apiRequest(
  path: string,
  init: ApiRequestInit
): Promise<Response> {
  const res = await sendApiRequest(path, init);
  if (!res.ok) {
    const { label } = init;
    const responseText = await res.text();
    throw new ApiRequestError({
      detail: responseText || `${label} failed: ${res.status}`,
      status: res.status,
      // The one refusal every screen explains alike (ADR-0144).
      userMessage:
        connectRefusalFromText(responseText)?.code === "property_preparing"
          ? preparingMessage()
          : undefined,
    });
  }
  return res;
}

/**
 * {@link apiRequest} then `res.json()`. When a `schema` is passed the body is
 * validated with it (the shared contract is the source of truth, so a server
 * shape drift throws here instead of surfacing as a downstream `undefined`).
 */
export async function apiJson<T>(
  path: string,
  init: ApiRequestInit & { schema?: ZodType<T> }
): Promise<T> {
  const res = await apiRequest(path, init);
  const data = await res.json();
  return init.schema ? init.schema.parse(data) : (data as T);
}
